/**
 * Toast API — use showToast() from this module only. All toast IDs must come from @/config/toast-ids.
 * Same id replaces the previous toast (Sonner behavior).
 */
import { createElement } from 'react';
import { toast as sonnerToast } from 'sonner';
import { AnimatedButton, type ButtonVariant } from '@/components/ui/animated-button';
import { trackAction } from '@/lib/telemetry';
import { ToastPnlValue } from '@/lib/ui/toast-pnl-value';

const DEFAULT_DURATION_MS = 3000;

export type ToastVariant = 'default' | 'success' | 'error' | 'info' | 'warning' | 'loading';

export type ToastPosition =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export interface ShowToastParams {
  id: string;
  message: string;
  description?: string;
  variant?: ToastVariant;
  duration?: number;
  position?: ToastPosition;
  action?: { label: string; onClick: () => void };
  cancel?: { label: string; onClick?: () => void };
  actionLayout?: 'inline';
  onDismiss?: (data?: object) => void;
  onAutoClose?: (data?: object) => void;
  /** Realised PnL rendered as a colour-coded description (profit green, loss
   *  red, dust neutral). When set, it becomes the toast's description node. */
  pnl?: { amountD6: string; formatted: string };
}

interface ToastControl {
  label: string;
  onClick?: () => void;
}

function createToastButton(
  id: string,
  control: ToastControl,
  variant: Extract<ButtonVariant, 'primary' | 'secondary'>,
): React.ReactNode {
  return createElement(
    AnimatedButton,
    {
      variant,
      size: 'sm',
      className: 'w-full min-w-0',
      onClick: (): void => {
        control.onClick?.();
        sonnerToast.dismiss(id);
      },
    },
    control.label,
  );
}

function createInlineToastActions(
  id: string,
  action: NonNullable<ShowToastParams['action']>,
  cancel: NonNullable<ShowToastParams['cancel']>,
): React.ReactNode {
  return createElement(
    'div',
    {
      'data-toast-actions': '',
      className: 'grid w-full grid-cols-2 gap-2 px-2',
      style: { gridColumn: '1 / -1' },
    },
    createToastButton(id, action, 'primary'),
    createToastButton(id, cancel, 'secondary'),
  );
}

function buildOptions(params: ShowToastParams): Record<string, unknown> {
  const {
    id,
    description,
    duration = DEFAULT_DURATION_MS,
    position,
    action,
    cancel,
    actionLayout,
    onDismiss,
    onAutoClose,
    pnl,
  } = params;
  // A `pnl` value renders as a colour-coded description node, taking
  // precedence over a plain-string description.
  const resolvedDescription = pnl
    ? createElement(ToastPnlValue, { amountD6: pnl.amountD6, formatted: pnl.formatted })
    : description;
  const inlineActions = actionLayout === 'inline' && action !== undefined && cancel !== undefined;
  if (inlineActions) {
    return {
      id,
      description: resolvedDescription,
      duration,
      position,
      action: createInlineToastActions(id, action, cancel),
      cancel: undefined,
      style: {
        display: 'grid',
        gridTemplateColumns: '16px minmax(0, 1fr)',
        alignItems: 'start',
      },
      classNames: { content: 'min-w-0' },
      onDismiss,
      onAutoClose,
    };
  }

  return {
    id,
    description: resolvedDescription,
    duration,
    position,
    action,
    cancel,
    onDismiss,
    onAutoClose,
  };
}

export function showToast(params: ShowToastParams): void {
  const options = buildOptions(params);
  const { id, message, variant = 'default' } = params;

  // Centralised telemetry: EVERY error/warning snackbar lands on Better Stack,
  // regardless of which call site fired it. This is the uniform "an error was
  // shown to the user" signal; the global MutationErrorListener additionally
  // emits the richer `mutation_error` event (with apiCode/correlationId) for
  // SDK-mutation failures. `trackAction` no-ops server-side / when disabled.
  if (variant === 'error' || variant === 'warning') {
    trackAction(variant === 'error' ? 'toast_error_shown' : 'toast_warning_shown', {
      level: variant === 'error' ? 'error' : 'warn',
      toast_id: id,
      message,
    });
  }

  switch (variant) {
    case 'success':
      sonnerToast.success(message, options);
      break;
    case 'error':
      sonnerToast.error(message, options);
      break;
    case 'info':
      sonnerToast.info(message, options);
      break;
    case 'warning':
      sonnerToast.warning(message, options);
      break;
    case 'loading':
      sonnerToast.loading(message, options);
      break;
    default:
      sonnerToast(message, options);
      break;
  }
}

/**
 * Dismiss a toast by id. Use for loading toasts that are closed when the operation completes.
 */
export function dismissToast(id: string): void {
  sonnerToast.dismiss(id);
}
